import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, ZoomIn } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useMeta } from "@/lib/useMeta";
import { CAT_KEYS, LISTING_ANCHORS, lines, media } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";
import { PageHeader } from "@/components/site/Layout";
import { Lightbox } from "@/components/site/Lightbox";

const FormBtn = ({ children, testid }) => (
  <Link to="/listing-questionnaire" data-testid={testid} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white text-sm font-bold rounded-full px-6 py-3 transition-colors">
    <ClipboardList className="w-4 h-4" />{children}
  </Link>
);

const WIDE = ["cat1", "cat2"];

const Category = ({ k, cat, L, alt, onZoom }) => {
  const anchor = LISTING_ANCHORS[k];
  const side = cat.sample_image && !WIDE.includes(k);
  return (
    <section id={anchor} className={`scroll-mt-24 py-16 sm:py-24 ${alt ? "bg-ivory-alt" : "bg-white"}`} data-testid={`listing-section-${anchor}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 grid gap-12 items-start ${side ? "lg:grid-cols-[1.2fr_1fr]" : ""}`}>
        <Reveal className="min-w-0">
          <span className="block h-[2px] w-12 bg-brass mb-5" />
          <h2 className="text-3xl sm:text-4xl text-charcoal" data-testid={`listing-title-${anchor}`}>{cat.title}</h2>
          <p className="mt-5 text-stone-600 leading-relaxed text-sm sm:text-base max-w-3xl">{cat.description}</p>
          <p className="mt-7 font-bold text-charcoal text-sm">{cat.highlights_label}</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-3xl">
            {lines(cat.highlights_lines).map((h, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-stone-700"><Check className="w-4 h-4 mt-0.5 shrink-0 text-crimson" />{h}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <div className="border-l-4 border-brass bg-white shadow-sm px-6 py-4 rounded-r-md" data-testid={`listing-price-${anchor}`}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">{L.price_label}</div>
              <div className="font-serif text-2xl sm:text-3xl text-crimson mt-1">{cat.price}</div>
            </div>
            <FormBtn testid={`listing-form-btn-${anchor}`}>{L.cat_button}</FormBtn>
          </div>
        </Reveal>
        {cat.sample_image && (
          <Reveal delay={0.1}>
            <button onClick={() => onZoom(media(cat.sample_image))} className="group block w-full text-left" data-testid={`listing-sample-${anchor}`}>
              <img src={media(cat.sample_image)} alt={cat.sample_caption} className="block max-w-full h-auto mx-auto shadow-lg group-hover:shadow-2xl transition-shadow" style={{ width: `${Number(cat.sample_image_width) || 100}%` }} />
              <span className="mt-3 flex items-center gap-2 text-xs font-bold text-crimson tracking-wide"><ZoomIn className="w-4 h-4" />{cat.sample_caption} · {L.enlarge_label}</span>
            </button>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default function ListingOptions() {
  const { content: c } = useSite();
  useMeta("listing");
  const L = c.listing;
  const { hash } = useLocation();
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="listing-options-page">
      <PageHeader title={L.heading} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <Rich html={L.intro_html} className="text-stone-700 leading-relaxed max-w-4xl text-sm sm:text-base" testid="listing-intro" />
        <h3 className="mt-10 text-xl text-charcoal">{L.nav_label}</h3>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="listing-pill-nav">
          {CAT_KEYS.map((k) => (
            <a key={k} href={`#${LISTING_ANCHORS[k]}`} data-testid={`listing-pill-${LISTING_ANCHORS[k]}`} className="text-sm border border-stone-300 bg-white rounded-full px-4 py-2 hover:border-crimson hover:text-crimson transition-colors">{c[k].title}</a>
          ))}
        </div>
        <div className="mt-8"><FormBtn testid="listing-top-form-btn">{L.top_button}</FormBtn></div>
      </section>
      {CAT_KEYS.map((k, i) => <Category key={k} k={k} cat={c[k]} L={L} alt={i % 2 === 0} onZoom={setZoom} />)}
      <section className="bg-charcoal text-stone-300 py-16 sm:py-20" data-testid="listing-closing">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl text-white leading-snug">{L.closing_title}</h2>
          <Rich html={L.closing_html} className="mt-5 text-sm sm:text-base leading-relaxed [&_a]:text-brass" />
          <div className="mt-8"><Link to="/listing-questionnaire" className="inline-flex items-center gap-2 text-white font-bold text-sm hover:text-brass" data-testid="listing-closing-form-link">{L.cat_button}<ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
      <Lightbox src={zoom} onClose={() => setZoom(null)} testid="listing-lightbox" />
    </div>
  );
}
